import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ChangeEvent } from 'react';
import { Autocomplete, TextInput } from '@mantine/core';
import { DateInput } from '@mantine/dates';
import {
  IconCalendar,
  IconMapPin,
  IconSearch,
  IconFlag,
  IconListCheck,
} from '@tabler/icons-react';
import { useShallow } from 'zustand/react/shallow';
import { useTranslation } from 'react-i18next';
import { SEARCH_DEBOUNCE_DELAY } from '@/constants';
import { ICON_SIZE_SM } from '@/constants/ui';
import { Z_INDEX_MOBILE_MODAL_DATEPICKER } from '@/constants/zIndex';
import { useDebounce, useFilterState } from '@/hooks';
import { useGeoMarkupStore } from '@/store';
import {
  selectHasActiveTaskFilters,
  selectHasActiveTaskFiltersInMapPanel,
  selectUniqueLocations,
} from '@/store/selectors';
import { getDateInputPattern } from '@/config/dateLocales';
import { TaskStatus, Priority } from '@/types';
import { CollapsingMultiSelect } from '@/components/ui/CollapsingMultiSelect';
import { ClearFiltersButton } from '@/components/ui/ClearFiltersButton';
import { SortControls } from '@/components/ui/SortControls';
import { MemberFilterMultiSelect } from '@/components/common/MemberFilterMultiSelect';
import { TASK_SORT_OPTIONS } from '@/constants/sortOptions';
import type { TaskSortField } from '@/store/slices/taskFiltersSlice';
import styles from './TaskFilters.module.css';

interface TaskFiltersProps {
  readonly showSearch?: boolean;
  readonly hideStatus?: boolean;
  readonly showSort?: boolean;
}

export const TaskFilters = memo(function TaskFilters({
  showSearch = false,
  hideStatus = false,
  showSort = true,
}: TaskFiltersProps) {
  const { t, i18n } = useTranslation();
  const { getStatusData, getPriorityData } = useFilterState();

  // The map side panel renders without the search field (search lives in the
  // floating map SearchBar). `!showSearch` is the established map-panel signal.
  const isMapPanel = !showSearch;

  // --- Store state (atomic selectors) ---
  const search = useGeoMarkupStore((state) => state.taskFilters.search);
  const status = useGeoMarkupStore(
    useShallow((state) => state.taskFilters.status)
  );
  const priority = useGeoMarkupStore(
    useShallow((state) => state.taskFilters.priority)
  );
  const assignedToId = useGeoMarkupStore(
    useShallow((state) => state.taskFilters.assignedToId)
  );
  const dateFrom = useGeoMarkupStore((state) => state.taskFilters.dateFrom);
  const dateTo = useGeoMarkupStore((state) => state.taskFilters.dateTo);
  const location = useGeoMarkupStore((state) => state.taskFilters.location);
  const hasActiveFilters = useGeoMarkupStore(
    isMapPanel
      ? selectHasActiveTaskFiltersInMapPanel
      : selectHasActiveTaskFilters
  );
  const uniqueLocations = useGeoMarkupStore(useShallow(selectUniqueLocations));

  // --- Store actions ---
  const setSearch = useGeoMarkupStore((state) => state.taskFilters.setSearch);
  const setStatus = useGeoMarkupStore((state) => state.taskFilters.setStatus);
  const setPriority = useGeoMarkupStore(
    (state) => state.taskFilters.setPriority
  );
  const setAssignedToId = useGeoMarkupStore(
    (state) => state.taskFilters.setAssignedToId
  );
  const setDateRange = useGeoMarkupStore(
    (state) => state.taskFilters.setDateRange
  );
  const setLocation = useGeoMarkupStore(
    (state) => state.taskFilters.setLocation
  );
  const resetFilters = useGeoMarkupStore(
    (state) => state.taskFilters.resetFilters
  );
  const sortBy = useGeoMarkupStore((state) => state.taskFilters.sortBy);
  const sortDescending = useGeoMarkupStore(
    (state) => state.taskFilters.sortDescending
  );
  const setSortBy = useGeoMarkupStore((state) => state.taskFilters.setSortBy);
  const setSortDescending = useGeoMarkupStore(
    (state) => state.taskFilters.setSortDescending
  );

  const handleSortByChange = useCallback(
    (value: string) => setSortBy(value as TaskSortField),
    [setSortBy]
  );

  // --- Search debounce ---
  const [localSearch, setLocalSearch] = useState(search);
  const { debouncedValue: debouncedSearch } = useDebounce(
    localSearch,
    SEARCH_DEBOUNCE_DELAY
  );
  const isExternalSearchChange = useRef(false);

  useEffect(() => {
    isExternalSearchChange.current = true;
    setLocalSearch(search);
  }, [search]);

  useEffect(() => {
    if (isExternalSearchChange.current) {
      isExternalSearchChange.current = false;
      return;
    }
    if (debouncedSearch !== search) {
      setSearch(debouncedSearch);
    }
  }, [debouncedSearch, search, setSearch]);

  const handleSearchChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setLocalSearch(event.currentTarget.value);
    },
    []
  );

  // --- Status options (multi-select; selecting Archived reveals archived) ---
  const statusOptions = useMemo(() => getStatusData(), [getStatusData]);

  const handleStatusChange = useCallback(
    (values: string[]) => setStatus(values as TaskStatus[]),
    [setStatus]
  );

  // --- Priority options (multi-select) ---
  const priorityOptions = useMemo(() => getPriorityData(), [getPriorityData]);

  const handlePriorityChange = useCallback(
    (values: string[]) => setPriority(values as Priority[]),
    [setPriority]
  );

  // --- Date handling ---
  const dateFormat = useMemo(
    () => getDateInputPattern(i18n.language).pattern,
    [i18n.language]
  );

  const handleDateFromChange = useCallback(
    (value: string | Date | null) => {
      if (value === null) {
        setDateRange(null, dateTo);
      } else if (typeof value === 'string') {
        const parsed = new Date(value);
        setDateRange(isNaN(parsed.getTime()) ? null : parsed, dateTo);
      } else {
        setDateRange(value, dateTo);
      }
    },
    [setDateRange, dateTo]
  );

  const handleDateToChange = useCallback(
    (value: string | Date | null) => {
      if (value === null) {
        setDateRange(dateFrom, null);
      } else if (typeof value === 'string') {
        const parsed = new Date(value);
        setDateRange(dateFrom, isNaN(parsed.getTime()) ? null : parsed);
      } else {
        setDateRange(dateFrom, value);
      }
    },
    [setDateRange, dateFrom]
  );

  return (
    <div className={styles.container}>
      <div
        className={`${styles.fieldsRow} ${isMapPanel ? styles.fieldsRowPanel : ''}`}
      >
        {showSearch && (
          <TextInput
            className={styles.fieldWide}
            label={t('taskViews.list.filters.title')}
            placeholder={t('search.placeholder')}
            leftSection={<IconSearch size={ICON_SIZE_SM} />}
            value={localSearch}
            onChange={handleSearchChange}
          />
        )}

        {!hideStatus && (
          <CollapsingMultiSelect
            className={styles.fieldNarrow}
            label={t('filters.statusLabel')}
            placeholder={t('filters.statusPlaceholder')}
            data={statusOptions}
            value={status}
            onChange={handleStatusChange}
            leftSection={<IconListCheck size={ICON_SIZE_SM} />}
            clearable
            comboboxProps={{
              zIndex: Z_INDEX_MOBILE_MODAL_DATEPICKER,
              withinPortal: true,
            }}
          />
        )}

        <CollapsingMultiSelect
          className={styles.fieldNarrow}
          label={t('common.priority')}
          placeholder={t('filters.priorityPlaceholder')}
          data={priorityOptions}
          value={priority}
          onChange={handlePriorityChange}
          leftSection={<IconFlag size={ICON_SIZE_SM} />}
          clearable
          comboboxProps={{
            zIndex: Z_INDEX_MOBILE_MODAL_DATEPICKER,
            withinPortal: true,
          }}
        />

        <MemberFilterMultiSelect
          className={styles.fieldWide}
          label={t('filters.assignee')}
          placeholder={t('filters.assigneePlaceholder')}
          value={assignedToId}
          onChange={setAssignedToId}
        />

        <DateInput
          className={styles.dateField}
          label={t('filters.dateFrom')}
          placeholder={dateFormat}
          valueFormat={dateFormat}
          value={dateFrom}
          onChange={handleDateFromChange}
          leftSection={<IconCalendar size={ICON_SIZE_SM} />}
          clearable
          maxDate={dateTo ?? undefined}
          popoverProps={{
            zIndex: Z_INDEX_MOBILE_MODAL_DATEPICKER,
            withinPortal: true,
          }}
        />

        <DateInput
          className={styles.dateField}
          label={t('filters.dateTo')}
          placeholder={dateFormat}
          valueFormat={dateFormat}
          value={dateTo}
          onChange={handleDateToChange}
          leftSection={<IconCalendar size={ICON_SIZE_SM} />}
          clearable
          minDate={dateFrom ?? undefined}
          popoverProps={{
            zIndex: Z_INDEX_MOBILE_MODAL_DATEPICKER,
            withinPortal: true,
          }}
        />

        <Autocomplete
          className={styles.fieldWide}
          label={t('filters.location')}
          placeholder={t('filters.locationPlaceholder')}
          data={uniqueLocations}
          value={location}
          onChange={setLocation}
          leftSection={<IconMapPin size={ICON_SIZE_SM} />}
          clearable
          limit={10}
          comboboxProps={{
            zIndex: Z_INDEX_MOBILE_MODAL_DATEPICKER,
            withinPortal: true,
          }}
        />
      </div>

      {showSort && (
        <div className={styles.sortRow}>
          <SortControls
            options={TASK_SORT_OPTIONS}
            sortBy={sortBy}
            sortDescending={sortDescending}
            onSortByChange={handleSortByChange}
            onSortDescendingChange={setSortDescending}
          />
        </div>
      )}

      {hasActiveFilters && (
        <div
          className={`${styles.clearRow} ${isMapPanel ? styles.clearRowCentered : ''}`}
        >
          <ClearFiltersButton onClick={resetFilters} />
        </div>
      )}
    </div>
  );
});
